import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useLiveData } from "hooks";
import { Page } from "modules/layout/Page";
import { FirebaseShard } from "firebaseWrapper";
import dayjs from "dayjs-local";

import { DeviceStatus } from "../components/DeviceStatus";

interface ConfigValueBool {
  default: boolean;
  override?: boolean;
  newValue?: boolean;
}

interface ConfigValueNumber {
  max: number;
  min: number;
  default: number;
  override?: number;
  newValue?: number;
}

type ConfigValue = ConfigValueBool | ConfigValueNumber;

interface ConfigDataHeader {
  schema: number;
}
type ConfigData = ConfigDataHeader & {
  [group: string]: { [name: string]: ConfigValue };
};

interface ConfigMessage {
  timestamp_utc: string;
  config: ConfigData;
}

function format_number(input: number, places: number): string {
  return input.toFixed(Number.isInteger(input) ? 0 : places);
}

export const ConfigField = ({
  group,
  name,
  details,
}: {
  group: string;
  name: string;
  details: ConfigValue;
}) => {
  const renderInput = (v: number | string | boolean | undefined) => {
    if (v === undefined) {
      return "";
    }
    if (typeof v === "number") {
      return format_number(v, 3);
    } else if (typeof v === "boolean") {
      return v ? "Enabled" : "Disabled";
    } else {
      return v;
    }
  };

  return (
    //even:bg-gray-100
    <tr key={name} className=" bg-white">
      <td className="px-6 py-1 whitespace-no-wrap border-b border-gray-200">
        <div className="flex items-center">
          <div className="ml-4">
            <div className="text-sm font-medium leading-5 text-gray-900">
              {group}
            </div>
          </div>
        </div>
      </td>

      <td className="px-6 py-1 whitespace-no-wrap border-b border-gray-200">
        <div className="flex items-center">
          <div className="ml-4">
            <div className="text-sm font-medium leading-5 text-gray-900">
              {name}
            </div>
          </div>
        </div>
      </td>

      <td className="px-6 py-1 whitespace-no-wrap border-b border-gray-200">
        <div className="text-sm leading-5 ">{renderInput(details.default)}</div>
      </td>

      <td className="px-6 py-1 whitespace-no-wrap border-b border-gray-200">
        <div className="text-sm leading-5 ">
          {renderInput(details.override)}
        </div>
      </td>

      {/* <td className="px-6 py-1 text-sm leading-5 whitespace-no-wrap border-b border-gray-200">
        <button>Update</button>
      </td> */}
    </tr>
  );
};

export const ConfigPage = () => {
  const { siteId, unitId, deviceId } = useParams();
  const [latestData, setLatestData] = useState<ConfigMessage[]>([]);

  useLiveData(
    {
      maxDataPoints: 1,
      firebaseShard: FirebaseShard.Tracing,
      firebaseRef: `devices/dataBySite/${siteId}/historyByDevice/${unitId}/${deviceId}/data/config_data`,
      orderByDescending: true,
      onInitialData: setLatestData,
      onIncrementalData: setLatestData,
    },
    [deviceId]
  );

  // DON"T LIKE THIS but gets the job done. FIX
  if (latestData.length === 0) return <p>Loading...</p>;

  const data = latestData[0];

  const last_updated = data.timestamp_utc;
  const config = data.config;

  return (
    <Page
      titleSupplement={
        <DeviceStatus
          siteId={siteId}
          unitId={unitId}
          deviceId={deviceId}
          className="ml-2"
        />
      }
    >
      <div className="flex justify-between">
        <h1>
          <span className="font-bold">Latest updated:</span>{" "}
          {dayjs.utc(last_updated).fromNow()}
        </h1>
      </div>

      <div className="flex flex-col mt-8">
        <div className="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg">
            <table className="">
              <thead>
                <tr>
                  {["Group", "Name", "Default", "Override"].map((t) => (
                    <th
                      key={t}
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {Object.entries(config).map(([group_name, fields]) => {
                  return Object.entries(fields).map(([name, details]) => {
                    const d = details as ConfigValue;
                    return ConfigField({
                      group: group_name,
                      name: name,
                      details: d,
                    });
                  });
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Page>
  );
};
